// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A visual indicator component for displaying asset functional status.
 * This module provides a flexible status indicator that can be displayed
 * either as an icon or a chip, with optional tooltips for additional context.
 * The component uses color and iconography to clearly communicate the
 * operational state of an asset.
 *
 * Key features:
 * - Multiple display variants (icon/chip)
 * - Semantic color coding
 * - Hover tooltips with descriptive text
 * - Accessibility support
 * - Consistent status representation
 */
import {
  ASSET_FUNCTIONAL_STATUS_LABELS,
  AssetFunctionalStatus,
} from '@/constants/asset-functional-status';
import {
  CheckCircleOutlineRounded,
  DeleteRounded,
  ErrorOutlineRounded,
  WarningAmberOutlined,
} from '@/constants/hardfin-standard-icons';
import { Chip, Tooltip } from '@mui/material';
import { ReactElement } from 'react';

/**
 * Display variants for the status indicator.
 * - 'icon': Compact icon-only display
 * - 'chip': Full chip with label and icon
 */
type AssetFunctionalStatusIndicatorVariant = 'icon' | 'chip';

/**
 * Mapping of status values to their corresponding icons.
 * Each status is represented by a Material-UI icon with appropriate color.
 */
const ICONS: Record<AssetFunctionalStatus, ReactElement> = {
  SCRAPPED: <DeleteRounded fontSize="small" color="disabled" />,
  FUNCTIONAL: <CheckCircleOutlineRounded fontSize="small" color="success" />,
  NEEDS_REVIEW: <WarningAmberOutlined fontSize="small" color="warning" />,
  NON_FUNCTIONAL: <ErrorOutlineRounded fontSize="small" color="error" />,
};

/**
 * Mapping of status values to semantic colors.
 * These colors are used for both icons and chips to maintain consistency.
 */
const COLORS: Record<
  AssetFunctionalStatus,
  'success' | 'error' | 'default' | 'warning'
> = {
  SCRAPPED: 'default',
  FUNCTIONAL: 'success',
  NEEDS_REVIEW: 'warning',
  NON_FUNCTIONAL: 'error',
};

/**
 * Props for configuring the AssetFunctionalStatusIndicator component.
 * @typedef {Object} Props
 */
interface Props {
  /**
   * The functional status of the asset.
   * Determines the color and icon used in the indicator.
   *
   * Possible values:
   * - 'FUNCTIONAL': Asset is operational (green)
   * - 'NON_FUNCTIONAL': Asset has issues (red)
   * - 'SCRAPPED': Asset is decommissioned (gray)
   */
  status: AssetFunctionalStatus;

  /**
   * The variant of the indicator.
   * Controls how the status is displayed visually.
   *
   * Options:
   * - 'icon': Shows only the status icon (default)
   * - 'chip': Shows a chip with both icon and label
   *
   * @default 'icon'
   */
  variant?: AssetFunctionalStatusIndicatorVariant;

  /**
   * Whether to show a tooltip on hover.
   * When enabled, displays a helpful description of the status.
   *
   * Tooltip text:
   * - FUNCTIONAL: "Asset is okay"
   * - NON_FUNCTIONAL: "Asset has error"
   * - SCRAPPED: "Asset has been scrapped"
   *
   * @default true
   */
  tooltip?: boolean;
}

/**
 * A component that visually represents an asset's functional status.
 * Provides immediate visual feedback about an asset's operational state
 * through color-coded icons or chips with optional tooltips.
 *
 * Features:
 * - Color-coded status representation
 * - Multiple display variants (icon/chip)
 * - Hover tooltips with status descriptions
 * - Accessible status information
 * - Consistent visual language
 *
 * Visual States:
 * - Functional: Green checkmark indicating operational status
 * - Non-functional: Red error icon indicating issues
 * - Scrapped: Gray delete icon indicating decommissioned state
 *
 * Usage example:
 * ```tsx
 * // Icon variant (default)
 * <AssetFunctionalStatusIndicator status="FUNCTIONAL" />
 *
 * // Chip variant with tooltip disabled
 * <AssetFunctionalStatusIndicator
 *   status="NON_FUNCTIONAL"
 *   variant="chip"
 *   tooltip={false}
 * />
 * ```
 *
 * @param props - Configuration options for the indicator
 */
export function AssetFunctionalStatusIndicator(props: Props) {
  const icon = ICONS[props.status];
  const label = ASSET_FUNCTIONAL_STATUS_LABELS[props.status];
  const color = COLORS[props.status];
  const title =
    props.tooltip === false
      ? null
      : props.status === 'FUNCTIONAL'
        ? 'Asset is okay'
        : props.status === 'NEEDS_REVIEW'
          ? 'Asset is pending'
          : props.status === 'SCRAPPED'
            ? 'Asset has been scrapped'
            : 'Asset has error';

  if (props.variant === 'chip') {
    return (
      <Tooltip arrow placement="right" title={title}>
        <Chip size="small" icon={icon} color={color} label={label} />
      </Tooltip>
    );
  }

  return (
    <Tooltip arrow placement="right" title={title}>
      {icon}
    </Tooltip>
  );
}
